import React, { useMemo, Fragment } from "react";
import { Box, Button, css, Icon, Stack, Text } from "@storyofams/react-ui";
import { includes } from "lodash";
import { usePostHog } from "posthog-js/react";
import { FormattedMessage, useIntl, defineMessages } from "react-intl";
import { Divider } from "~components/common/Divider";
import { NavUnderline } from "~components/common/Icons";
import { Title } from "./Instructions";
import { TableItem } from "./TableItem";

const messages = defineMessages({
  natuurlijk: "100% natuurlijk",
  vegetarisch: "Vegetarisch",
  vegan: "Vegan",
  crueltyFree: "Proefdiervrij",
  foodgrade: "Foodgrade",
  biologisch: "Biologisch",
  ongeraffineerd: "Ongeraffineerd",
  fairtrade: "Fair trade",
  alcohol_vrij: "Vrij van alcohol",
  parfumvrij: "Vrij van (synthetische) parfum",
  conserveringsmiddelen: "Vrij van conserveringsmiddelen",
  aardolie: "Vrij van minerale oliën (aardolie afgeleiden)",
  rotzooi:
    "Vrij van ftalaten, parabenen, minerale oliën, nano-deeltjes, SLS, triclosan, talc, siliconen, aluminium, Bisfenolen (BPA), PEG's, DEA, TEA, MEA",
  houdbaarheid: "Houdbaarheid (vanaf productiedatum)",
  months: "maanden",
  ingredients: "Ingrediënten",
  instructions: "Gebruiksaanwijzing",
  material: "Grondstof",
  warnings: "Waarschuwingen",
  characteristics: "Eigenschappen",
  substantie: "Substantie",
  afkomst: "Afkomst",
  consistentie: "Consistentie",
  kleur: "Kleur",
  geur: "Geur",
  oplosbaar: "Oplosbaar in",
  note: "Noot",
  botanische_naam: "Botanische naam",
  methode: "Methode",
  plantendeel: "Plantendeel",
  plantfamilie: "Plant familie",
  ewg_cijfer: "EWG score",
});

export const Specifications = ({ getValue, product }) => {
  const intl = useIntl();
  const posthog = usePostHog();
  const msds = product?.metafields?.find((item) => item?.key === "msds")?.value;
  const materials = useMemo(
    () =>
      [
        { name: "substantie" },
        { name: "afkomst" },
        { name: "consistentie" },
        { name: "kleur" },
        { name: "geur" },
        { name: "oplosbaar", onlyVisibleIfTrue: true },
        {
          name: "note",
          onlyVisibleIfTrue: true,
          dependent: [
            { name: "botanische_naam" },
            { name: "methode" },
            { name: "plantendeel" },
            { name: "plantfamilie" },
          ].map((item) => ({
            ...item,
            value: getValue(item.name),
            label: intl.formatMessage(messages[item.name]),
          })),
        },
        {
          name: "ewg_cijfer",
          onlyVisibleIfTrue: true,
          url: getValue("ewgurl"),
        },
      ].map((item) => ({
        ...item,
        value: getValue(item.name),
        label: intl.formatMessage(messages[item.name]),
      })),
    [product.metafields]
  );
  const characteristics = useMemo(
    () =>
      [
        { name: "natuurlijk" },
        { name: "vegetarisch" },
        { name: "vegan" },
        { name: "crueltyFree" },
        { name: "foodgrade", onlyVisibleIfTrue: true },
        { name: "biologisch" },
        { name: "ongeraffineerd", onlyVisibleIfTrue: true },
        { name: "fairtrade", onlyVisibleIfTrue: true },
        { name: "alcohol_vrij" },
        { name: "parfumvrij" },
        { name: "conserveringsmiddelen" },
        { name: "aardolie", onlyVisibleIfTrue: true },
        { name: "rotzooi" },
        { name: "houdbaarheid", onlyVisibleIfTrue: true },
      ].map(({ name, onlyVisibleIfTrue }) => {
        switch (name) {
          case "crueltyFree":
            return {
              name,
              onlyVisibleIfTrue,
              label: intl.formatMessage(messages[name]),
              value: true,
            };

          case "houdbaarheid":
            return {
              name,
              onlyVisibleIfTrue,
              label: intl.formatMessage(messages[name]),
              value: getValue(name),
            };

          default:
            return {
              name,
              onlyVisibleIfTrue,
              label: intl.formatMessage(messages[name]),
              value: getValue(name) === "true",
            };
        }
      }),
    [product?.metafields]
  );

  const ingredients = getValue("ingredienten_nieuw");
  const inci = getValue("inci_nieuw");
  const ghsHazard = getValue("ghs_hazard")
    ?.split("|")
    .map((item) => item.substr(item.indexOf("@") + 1))
    .join(" ");
  return (
    <>
      <Stack space={[5, 100]} flexDirection={["column", "row"]} mt={[2, 0]}>
        <Stack flex={1} space={2} flexDirection="column">
          {!!ingredients && (
            <>
              <Title>
                <FormattedMessage {...messages.ingredients} />
              </Title>
              <Text>{ingredients}</Text>
            </>
          )}

          {!!inci && (
            <>
              <Title>INCI</Title>
              <Text>{inci}</Text>
            </>
          )}

          {includes(
            ["Cosmetica", "Grondstof", "Merk"],
            getValue("wettelijk_type")
          ) && (
            <>
              {!!materials?.length && (
                <>
                  <Title>
                    <FormattedMessage {...messages.material} />
                  </Title>

                  <Box width="100%">
                    {materials.map(
                      ({ onlyVisibleIfTrue, dependent, ...props }, i) =>
                        !onlyVisibleIfTrue ||
                        (onlyVisibleIfTrue && !!props.value) ? (
                          <Fragment key={`materials-${props.name}`}>
                            <TableItem {...props} valueW={"40%"} />

                            {i < materials?.length - 1 && (
                              <Divider
                                my={1.25}
                                color="grey800"
                                opacity={0.16}
                              />
                            )}

                            {dependent &&
                              dependent?.map((item) => (
                                <Fragment key={`materials-${item.name}`}>
                                  <TableItem {...item} valueW={"40%"} />

                                  {i < materials?.length - 1 && (
                                    <Divider
                                      my={1.25}
                                      color="grey800"
                                      opacity={0.16}
                                    />
                                  )}
                                </Fragment>
                              ))}
                          </Fragment>
                        ) : null
                    )}
                  </Box>
                </>
              )}
            </>
          )}
          {!!ghsHazard && (
            <>
              <Title>Wettelijke tekst</Title>
              <Text>{ghsHazard}</Text>
            </>
          )}
        </Stack>

        {includes(
          ["Cosmetica", "Grondstof", "Merk", "Pakket"],
          getValue("wettelijk_type")
        ) && (
          <Box flex={1}>
            {characteristics.map(({ onlyVisibleIfTrue, ...props }, i) => {
              return !onlyVisibleIfTrue ||
                (onlyVisibleIfTrue && !!props.value) ? (
                <Fragment key={`characteristics-${props.name}`}>
                  <TableItem {...props} useIcon />

                  {i < characteristics?.length - 1 && (
                    <Divider my={1.25} color="grey800" opacity={0.16} />
                  )}
                </Fragment>
              ) : null;
            })}
          </Box>
        )}
      </Stack>
      {msds && (
        <Box width={["100%", "45%"]} mt={2}>
          <Title>Technisch</Title>
          <Stack space={[4, 10]} mt={2}>
            <Box flex={1}>
              <Text maxWidth={[294, "initial"]}>MSDS</Text>
            </Box>

            <Box width={"40%"} pl={[0, 0, 0, "1%"]}>
              <Button
                display="inline"
                cursor="pointer"
                css={css({
                  ".underline": {
                    position: "absolute",
                    right: "-4px",
                    height: 12,
                    bottom: 0,
                    width: "calc(100% + 8px)",
                    transition: "width 0.32s ease-in-out",
                    zIndex: -1,
                    svg: {
                      width: "100%",
                      height: "100%",
                      color: "sand",
                    },
                  },
                  "&&:hover": {
                    ".underline": {
                      width: "0",
                    },
                  },
                })}
              >
                <a
                  href={msds}
                  target="_blank"
                  rel="noreferrer"
                  // onClick={() =>
                  //   posthog.capture("Clicked Product Page Menu", {
                  //     subItem: "MSDS",
                  //   })
                  // }
                >
                  <Text fontWeight="medium">Download</Text>
                </a>
                <Icon className="underline" icon={NavUnderline} width="100%" />
              </Button>
            </Box>
          </Stack>
          <Divider my={1.25} color="grey800" opacity={0.16} />
        </Box>
      )}
    </>
  );
};
